import { IconCheck, IconChevronDown } from '@tabler/icons-react';
import {
  type CSSProperties,
  type KeyboardEvent,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

export type DropdownOption<T extends string | number> = {
  value: T;
  label: string;
  /** Muted text on the right ("felső jobb első nagyőrlő"). */
  hint?: string;
  /** Options with the same group are listed under one heading. */
  group?: string;
};

type Props<T extends string | number> = {
  value: T | null;
  options: DropdownOption<T>[];
  onChange: (value: T) => void;
  /** Shown when nothing is selected. */
  placeholder?: string;
  /** For a visible <label htmlFor>. */
  id?: string;
  /** Accessible name when there is no visible label. */
  ariaLabel?: string;
  disabled?: boolean;
  /** field: form input · compact: in tables · bar: on the top bar · toolbar: centred, bold. */
  variant?: 'field' | 'compact' | 'bar' | 'toolbar';
};

const LIST_MAX = 320;
const TYPEAHEAD_MS = 600;
const PHONE = '(max-width: 640px)';

/**
 * Custom select: a button and a listbox, rendered at body level so tables and
 * sheets cannot clip it. On phones the list opens as a bottom panel (thumb
 * reach). Keyboard: arrows, Home/End, Enter/Space, Escape, Tab, type to jump.
 */
export function Dropdown<T extends string | number>({
  value,
  options,
  onChange,
  placeholder,
  id,
  ariaLabel,
  disabled,
  variant = 'field',
}: Props<T>) {
  const baseId = useId();
  const buttonId = id ?? `${baseId}-button`;
  const listId = `${baseId}-list`;
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [position, setPosition] = useState<CSSProperties>({});
  const buttonRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const typed = useRef({ text: '', at: 0 });

  const selectedIndex = options.findIndex((o) => o.value === value);
  const selected = selectedIndex >= 0 ? options[selectedIndex] : null;
  const valueText = selected?.label ?? placeholder ?? '';
  const valueId = `${baseId}-value`;

  const place = useCallback(() => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;
    // Phones: the stylesheet docks the list at the bottom of the screen.
    if (window.matchMedia(PHONE).matches) {
      setPosition({});
      return;
    }
    const below = window.innerHeight - rect.bottom;
    const up = below < Math.min(LIST_MAX, 240) && rect.top > below;
    // A button in the right half opens the list leftwards from its right edge,
    // so the labels are not cut off at the edge of the screen.
    // clientWidth: `right` is measured from the viewport without the scrollbar.
    const width = document.documentElement.clientWidth;
    const rightward = rect.left + rect.width / 2 <= width / 2;
    setPosition({
      ...(rightward ? { left: rect.left } : { right: width - rect.right }),
      minWidth: rect.width,
      maxHeight: Math.min(LIST_MAX, (up ? rect.top : below) - 12),
      ...(up ? { bottom: window.innerHeight - rect.top + 4 } : { top: rect.bottom + 4 }),
    });
  }, []);

  const close = useCallback((focusButton = true) => {
    setOpen(false);
    if (focusButton) buttonRef.current?.focus();
  }, []);

  const openList = () => {
    if (disabled || !options.length) return;
    setActive(selectedIndex >= 0 ? selectedIndex : 0);
    place();
    setOpen(true);
  };

  const choose = (index: number) => {
    const option = options[index];
    if (!option) return;
    // Like a native select: only a real change is reported.
    if (option.value !== value) onChange(option.value);
    close();
  };

  // The options can change while open (data refetched): keep the index valid.
  const activeIndex = Math.min(active, options.length - 1);
  useEffect(() => {
    if (open && options.length === 0) close(false);
  }, [open, options.length, close]);

  // Focus the list when it opens; keep the active option in view.
  useLayoutEffect(() => {
    if (open) listRef.current?.focus();
  }, [open]);
  useEffect(() => {
    if (!open) return;
    listRef.current
      ?.querySelector(`[data-index="${activeIndex}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [open, activeIndex]);

  // Follow the button while the page scrolls. Outside clicks land on the
  // backdrop, which closes the list and swallows the click, so it cannot also
  // close a sheet behind it or activate whatever is underneath.
  useEffect(() => {
    if (!open) return;
    const onScroll = (e: Event) => {
      if (!listRef.current?.contains(e.target as Node)) place();
    };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', place);
    };
  }, [open, place]);

  const typeAhead = (key: string) => {
    const now = Date.now();
    const text =
      (now - typed.current.at > TYPEAHEAD_MS ? '' : typed.current.text) + key.toLowerCase();
    typed.current = { text, at: now };
    const hit = options.findIndex((o) => o.label.toLowerCase().startsWith(text));
    if (hit >= 0) setActive(hit);
  };

  const MOVES: Record<string, (i: number) => number> = {
    ArrowDown: (i) => Math.min(options.length - 1, i + 1),
    ArrowUp: (i) => Math.max(0, i - 1),
    Home: () => 0,
    End: () => options.length - 1,
    PageDown: (i) => Math.min(options.length - 1, i + 8),
    PageUp: (i) => Math.max(0, i - 8),
  };

  const onListKey = (e: KeyboardEvent) => {
    const move = MOVES[e.key];
    if (move) {
      e.preventDefault();
      setActive(move);
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      choose(activeIndex);
    } else if (e.key === 'Escape') {
      // Close only the list, not a sheet around it.
      e.preventDefault();
      e.stopPropagation();
      close();
    } else if (e.key === 'Tab') {
      // Focus goes back to the button first, so Tab moves on from there.
      close();
    } else if (e.key.length === 1) {
      typeAhead(e.key);
    }
  };

  const onButtonKey = (e: KeyboardEvent) => {
    if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
      e.preventDefault();
      openList();
    }
  };

  return (
    <div className={`dropdown dropdown--${variant}`}>
      <button
        ref={buttonRef}
        id={buttonId}
        type="button"
        className="dropdown__button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        // The name must include the value: "Szék, 1. szék" rather than just "Szék".
        aria-label={ariaLabel ? `${ariaLabel}: ${valueText}` : undefined}
        aria-describedby={ariaLabel ? undefined : valueId}
        disabled={disabled}
        onClick={() => (open ? close() : openList())}
        onKeyDown={onButtonKey}
      >
        <span
          id={valueId}
          className={`dropdown__value ${selected ? '' : 'dropdown__value--placeholder'}`}
        >
          {valueText}
        </span>
        <IconChevronDown size={18} className="dropdown__chevron" aria-hidden="true" />
      </button>
      {open &&
        createPortal(
          <>
            <div
              className="dropdown__backdrop"
              aria-hidden="true"
              onClick={(e) => {
                e.stopPropagation();
                close(false);
              }}
            />
            <div
              ref={listRef}
              id={listId}
              className="dropdown__list"
              role="listbox"
              tabIndex={-1}
              aria-labelledby={ariaLabel ? undefined : buttonId}
              aria-label={ariaLabel}
              aria-activedescendant={activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined}
              style={position}
              onKeyDown={onListKey}
            >
              {groupRuns(options).map((run, r) => {
                const rows = run.items.map(({ option, index }) => (
                  <OptionRow
                    key={String(option.value)}
                    option={option}
                    id={`${listId}-${index}`}
                    index={index}
                    active={index === activeIndex}
                    selected={index === selectedIndex}
                    onHover={setActive}
                    onChoose={choose}
                  />
                ));
                if (!run.name) return rows;
                const headingId = `${listId}-group-${r}`;
                return (
                  // biome-ignore lint/a11y/useSemanticElements: ARIA listbox groups are role="group", a fieldset is not allowed here
                  <div key={headingId} role="group" aria-labelledby={headingId}>
                    <div id={headingId} className="dropdown__group" role="presentation">
                      {run.name}
                    </div>
                    {rows}
                  </div>
                );
              })}
            </div>
          </>,
          document.body
        )}
    </div>
  );
}

type RowProps<T extends string | number> = {
  option: DropdownOption<T>;
  id: string;
  index: number;
  active: boolean;
  selected: boolean;
  onHover: (index: number) => void;
  onChoose: (index: number) => void;
};

function OptionRow<T extends string | number>({
  option,
  id,
  index,
  active,
  selected,
  onHover,
  onChoose,
}: RowProps<T>) {
  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: the listbox handles the keyboard
    <div
      id={id}
      data-index={index}
      role="option"
      tabIndex={-1}
      aria-selected={selected}
      className={`dropdown__option ${active ? 'is-active' : ''}`}
      onPointerMove={() => onHover(index)}
      onClick={() => onChoose(index)}
    >
      <span className="dropdown__check" aria-hidden="true">
        {selected && <IconCheck size={16} />}
      </span>
      <span className="dropdown__label">{option.label}</span>
      {option.hint && <span className="dropdown__hint">{option.hint}</span>}
    </div>
  );
}

type Run<T extends string | number> = {
  name: string | null;
  items: { option: DropdownOption<T>; index: number }[];
};

/** Consecutive options with the same group, so each group renders as one role="group". */
function groupRuns<T extends string | number>(options: DropdownOption<T>[]): Run<T>[] {
  const runs: Run<T>[] = [];
  options.forEach((option, index) => {
    const name = option.group ?? null;
    const last = runs[runs.length - 1];
    if (last && last.name === name) last.items.push({ option, index });
    else runs.push({ name, items: [{ option, index }] });
  });
  return runs;
}
